<template>
    <section id="delivery">
        <div class="max-w-sm mx-auto mt-8">
            <delivery-address-form
                v-model="deliveryAddress"
                :error-handler="errorHandler"
                :enable-delivery-waiting-list="enableDeliveryWaitingList"/>
        </div>

        <div class="divider--container paper--container">
            <div class="paper">
                <div class="container mx-auto text-center">
                    <img :src="getImagetUrl" alt="Delivery" class="inline-block w-32">
                </div>

                <div class="clear--xs"/>

                <div class="container mx-auto text-center">
                <p v-if="deliveryDay && courierName"
                   v-html="$t('DELIVERY_FORM_YOUR_DELIVERIES', {deliveryDay: deliveryDay, courierName: courierName})"/>
                <p v-if="deliveryDay && deliveryAddress.city"
                   v-html="$t('FRESH_DELIVERY_IN_YOUR_CITY', {deliveryDay: deliveryDay, city: deliveryAddress.city})"/>
                <p v-else-if="postcodeError" v-text="$t(postcodeError)"/>
                <p v-else-if="!deliveryDay" v-html="$t('DELIVERY_FORM_POP_IN')"/>
                </div>
            </div>

            <div class="paper--background-container">
                <img class="paper--background-image--desktop" src="https://freddiesflowers-com.s3.eu-west-2.amazonaws.com/images/homepage/PAPER-BACKGROUND-DESKTOP.png" alt="Paper background" />
                <img class="paper--background-image--mobile" src="https://freddiesflowers-com.s3.eu-west-2.amazonaws.com/images/homepage/PAPER-BACKGROUND-MOBILE.png" alt="Paper background"  />
            </div>
        </div>

        <div class="max-w-sm mx-auto pb-5">
            <div v-if="allowFirstDateSelection">
                <delivery-day-field
                    v-model="selectedDeliveryDate"
                    :postcode="deliveryAddress.postcode"/>

                <!-- Small gap -->
                <div class="clear--sm"/>
            </div>

            <!--PropertyType-->
            <property-type-field
                v-model="propertyType"
                :is-valid="!errorHandler.has('delivery_address_property_type')"
                :mandatory="true"
                :message="$t(errorHandler.get('delivery_address_property_type'))"/>

            <!--Delivery comment-->
            <textbox-field
                v-if="isDEU"
                :label="$t('DELIVERY_DAY_FIELD_WHERE_SHALL_WE')"
                :placeholder="$t('DELIVERY_INSTRUCTIONS_FIELD_PLEASE_POP')"
                :is-valid="!errorHandler.has('delivery_instructions')"
                :message="$t(errorHandler.get('delivery_instructions'))"
                v-model="deliveryComment"
                :mandatory="true"
                :maxLength="50"/>

            <div class="clear--sm"/>

            <div class="px-3 mb-4">
                <p v-html="$t('DELIVERY_ADDRESS_PROPERTY_FIELD_AND_FINALLY')"/>
            </div>

            <telephone-field
                v-model="telephone"
                :is-valid="!errorHandler.has('telephone')"
                :message="$t(errorHandler.get('telephone'))"
                :mandatory="true"
                :should-validate="true"/>

            <!-- Small gap -->
            <div class="clear--sm"/>

            <checkbox
                v-model="optInMarketing"
                v-if="isUSA">
                {{ $t('ACCOUNT_FORM_I_WOULD_LIKE') }}
            </checkbox>

            <div class="py-1 px-5">
                <p class="input__message" v-if="getPauseIsCancel" v-html="$t('MARKETING_PREFERENCES_CONDITIONS')"></p>
            </div>

            <!-- Form button -->
            <div class="text-center">
                <submit-button
                    :processing="isFormProcessing"
                    color="green"
                    @click="submitCurrentStep()">
                    {{ $t('ACCOUNT_FORM_NEXT_STEP')}}
                </submit-button>
            </div>
        </div>
        <order-summary />
        <trust-pilot-review v-if="isDEU" step="delivery"/>
        <div class="clear--sm"></div>
        <supported-cards />
        <div class="clear--sm"></div>
    </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { SECTION_DELIVERY } from '@/constants/Registration';
import { isPostcodeFormatValid } from '@/helpers/PostcodeHelpers';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import RegistrationService from '@/services/users/RegistrationService';
import PostcodeService from '@/services/logistics/PostcodeService';
import TextboxField from '@/components/forms/fields/TextboxField';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import TelephoneField from '@/components/forms/fields/TelephoneField';
import DeliveryDayField from '@/components/forms/fields/DeliveryDayField';
import PropertyTypeField from '@/components/forms/fields/PropertyTypeField';
import DeliveryAddressForm from '@/components/forms/addresses/DeliveryAddressForm';
import { couriers } from '@/content/couriers';
import OrderSummary from '@/components/forms/fields/OrderSummary';
import Checkbox from '@/components/forms/fields/Checkbox';
import SupportedCards from '@/components/forms/fields/SupportedCards';
import TrustPilotReview from '@/components/forms/fields/TrustPilotReview';
import IncompleteRegistrationService from '@/services/users/incompleteRegistrationService/IncompleteRegistrationService';

export default {
    data: () => {
        return {
            postcodeError: null,
            isFormProcessing: false,
            errorHandler: new ErrorResponseHandler(),
            couponFieldEnabled: false,
        };
    },
    props: {
        enableDeliveryWaitingList: {
            type: Boolean,
            default: false,
        },
        allowFirstDateSelection: {
            type: Boolean,
            default: false,
        },
        enableIncompleteRegistration: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {
        const showCouponInputOnLoad = this.friendCode && this.friendCode.length > 0;
        this.couponFieldEnabled = showCouponInputOnLoad;
    },
    methods: {
        ...mapActions({
            fetchTaxes: 'register/fetchTaxes',
        }),
        fetchDeliveryDateFromApi(postcode) {
            if (isPostcodeFormatValid(postcode)) {
                PostcodeService.GetFirstDeliveryDayForPostcode(postcode)
                    .then(response => {
                        this.firstPossibleDeliveryDate = response.data.properties.first_delivery_day;
                        this.courierName = response.data.properties.courier_name;
                    })
                    .catch(error => {
                        const response = error.response.data;

                        if (response.errors && response.errors.postcode) {
                            this.postcodeError = response.errors.postcode[0];
                        }
                    });
            }
        },
        submitCurrentStep() {
            this.isFormProcessing = true;

            if (this.enableIncompleteRegistration) {
                setTimeout(() => (new IncompleteRegistrationService()).updateOrCreate(), 0);
            }

            RegistrationService.validateDelivery()
                .then(() => {
                    this.isFormProcessing = false;
                    this.$store.commit('register/setSectionComplete', SECTION_DELIVERY);
                    this.$router.push({ name: this.nextRouteName });
                })
                .catch(error => {
                    this.errorHandler.setResponse(error.response && error.response.data);
                    this.handleFail();
                });
        },

        handleFail() {
            this.isFormProcessing = false;
            this.$scrollTo('#delivery');
        },
    },
    computed: {
        ...mapGetters({
            deliveryDay: 'register/getFirstDeliveryDay',
            getCouponCode: 'register/getCoupon',
            isDEU: 'localisation/isDEU',
            isUSA: 'localisation/isUSA',
            isCanvasser: 'auth/isCanvasser',
            getPauseIsCancel: 'subscription/getPauseIsCancel',
        }),
        getImagetUrl() {
            let name = couriers[this.courierName] ? couriers[this.courierName].icon : 'van';
            return require(`@/assets/images/decoration/icon-delivery-${name}.svg`);
        },
        deliveryAddress: {
            get() {
                return this.$store.getters['register/getDeliveryAddress'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddress', value);
            },
        },
        firstPossibleDeliveryDate: {
            get() {
                return this.$store.getters['register/getFirstPossibleDeliveryDate'];
            },
            set(value) {
                this.$store.commit('register/setFirstPossibleDeliveryDate', value);
            },
        },
        courierName: {
            get() {
                return this.$store.getters['register/getCourierName'];
            },
            set(value) {
                this.$store.commit('register/setCourierName', value);
            },
        },
        selectedDeliveryDate: {
            get() {
                return this.$store.getters['register/getFirstDeliveryDateDesired'];
            },
            set(value) {
                this.$store.commit('register/setFirstDeliveryDateDesired', value);
            },
        },
        deliveryComment: {
            get() {
                return this.$store.getters['register/getDeliveryComment'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryComment', value);
            },
        },
        propertyType: {
            get() {
                return this.$store.getters['register/getPropertyType'];
            },
            set(value) {
                this.$store.commit('register/setPropertyType', value);
            },
        },
        telephone: {
            get() {
                return this.$store.getters['register/getTelephone'];
            },
            set(value) {
                this.$store.commit('register/setTelephone', value);
            },
        },
        friendCode: {
            get() {
                return this.$store.getters['register/getFriendCode'];
            },
            set(code) {
                this.$store.commit('register/setFriendCode', code);
            },
        },
        optInMarketing: {
            get() {
                return this.$store.getters['register/getOptInMarketing'];
            },
            set(value) {
                this.$store.commit('register/setOptInMarketing', value);
            },
        },
        nextRouteName() {
            return this.isCanvasser ? 'canvasser-register.card-details' : 'register.card-details';
        },
        submitButtonText() {
            return this.isCanvasser ? 'ACCOUNT_FORM_SIGN_UP' : 'ACCOUNT_FORM_NEXT_STEP';
        },
    },
    watch: {
        'deliveryAddress.postcode': {
            deep: true,
            handler: function(newPostcode, oldPostcode) {
                newPostcode = (newPostcode || '');
                oldPostcode = (oldPostcode || '');
                if ((oldPostcode !== newPostcode)) {
                    this.postcodeError = null;
                    this.firstPossibleDeliveryDate = null;

                    if (this.isUSA && newPostcode.length === 5) {
                        this.fetchTaxes({ postcode: newPostcode, friend_code: this.couponCode || this.friendCode });
                    }

                    this.fetchDeliveryDateFromApi(newPostcode);
                }
            },
        },
    },
    components: {
        DeliveryAddressForm,
        TextboxField,
        PropertyTypeField,
        TelephoneField,
        SubmitButton,
        DeliveryDayField,
        SupportedCards,
        OrderSummary,
        TrustPilotReview,
        Checkbox,
    },
};
</script>
